// Maestro de Herramientas — vista dedicada de las 4169+ herramientas de bodega.
// Muestra: código, descripción, familia, stock, precio, ubicación histórica
// (ULTUBICA del Access), último RUT asignado, origen. Filtros y botón QR.
import { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Wrench, Search, Loader2, QrCode, Printer, AlertCircle,
  DollarSign, Package, RefreshCw, MapPin, Filter, Download, History, FileSpreadsheet,
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useRegisterCmdKCommands } from '../../contexts/CmdKContext';
import { exportToCSV, printTable } from '../../lib/printUtils';

const fmtCLP = (v) => new Intl.NumberFormat('es-CL').format(Math.round(v || 0));

export default function MaestroHerramientasPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState('');
  const [qInput, setQInput] = useState('');
  const [familias, setFamilias] = useState([]);
  const [familiaFilter, setFamiliaFilter] = useState('');
  const [origenFilter, setOrigenFilter] = useState(''); // '', 'access_import', 'nativa'
  const [calidadFilter, setCalidadFilter] = useState(''); // '', 'desc_incompleta', 'sin_familia', 'sin_precio'
  const [selected, setSelected] = useState(new Set());
  const [stats, setStats] = useState({
    total_htas: 0, incompletas: 0, sin_familia: 0, sin_precio: 0, access_import: 0,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '1000' });
      if (q) params.set('q', q);
      if (familiaFilter) params.set('familia', familiaFilter);
      if (origenFilter) params.set('origen', origenFilter);
      if (calidadFilter) params.set('calidad', calidadFilter);
      const res = await api.get(`/bodega/maestro-herramientas?${params}`);
      setItems(res.data?.items || []);
      setTotal(res.data?.total || 0);
      setStats(res.data?.stats || {});
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error cargando herramientas');
    } finally {
      setLoading(false);
    }
  }, [q, familiaFilter, origenFilter, calidadFilter]);

  useEffect(() => { load(); }, [load]);

  // Carlos (10/05/2026): imprimir y exportar Maestro Herramientas
  const imprimirHtas = () => {
    if (!items.length) { toast.warning('Sin herramientas para imprimir'); return; }
    printTable({
      title: 'Maestro de Herramientas',
      subtitle: `${items.length} herramientas · ${familiaFilter || 'Todas familias'} · ${origenFilter || 'Todos orígenes'} · ${new Date().toLocaleString('es-CL')}`,
      summary: [
        { label: 'Total en sistema', value: stats.total_htas || total },
        { label: 'En archivo', value: items.length },
        { label: 'Sin familia', value: stats.sin_familia || 0, color: '#b91c1c' },
        { label: 'Sin precio', value: stats.sin_precio || 0, color: '#b45309' },
      ],
      columns: [
        { key: 'codigo', label: 'Código', mono: true },
        { key: 'descripcion', label: 'Descripción' },
        { key: 'familia', label: 'Familia' },
        { key: 'unidad', label: 'Unidad', align: 'center' },
        { key: 'stock', label: 'Stock', align: 'right', mono: true },
        { key: 'precio_unitario', label: 'Precio', align: 'right', mono: true, format: (v) => v ? `$${fmtCLP(v)}` : '-' },
        { key: 'ultima_ubicacion', label: 'Ubicación' },
        { key: 'origen', label: 'Origen', align: 'center' },
      ],
      rows: items,
    });
  };

  const exportarHtasExcel = () => {
    if (!items.length) { toast.warning('Sin herramientas para exportar'); return; }
    exportToCSV({
      filename: 'maestro_herramientas',
      title: 'CHIRINO STEEL · Maestro de Herramientas',
      summary: [
        { label: 'Total en sistema', value: stats.total_htas || total },
        { label: 'En archivo', value: items.length },
        { label: 'Búsqueda', value: q || '-' },
        { label: 'Familia', value: familiaFilter || 'Todas' },
        { label: 'Origen', value: origenFilter || 'Todos' },
        { label: 'Calidad', value: calidadFilter || '-' },
        { label: 'Generado', value: new Date().toLocaleString('es-CL') },
      ],
      columns: [
        { key: 'codigo', label: 'Código' },
        { key: 'descripcion', label: 'Descripción' },
        { key: 'familia', label: 'Familia' },
        { key: 'unidad', label: 'Unidad' },
        { key: 'stock', label: 'Stock', format: (v) => Number(v) || 0 },
        { key: 'precio_unitario', label: 'Precio (CLP)', format: (v) => Math.round(Number(v) || 0) },
        { key: 'ultima_ubicacion', label: 'Ubicación última' },
        { key: 'ultimo_rut_asignado', label: 'Último RUT asignado' },
        { key: 'origen', label: 'Origen' },
        { key: 'observaciones', label: 'Observaciones' },
      ],
      rows: items,
    });
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/bodega/familias-herramientas');
        setFamilias(res.data?.items || []);
      } catch { /* empty */ }
    })();
  }, []);

  const toggleSelect = (codigo) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(codigo)) next.delete(codigo); else next.add(codigo);
      return next;
    });
  };

  const descargarQRSeleccionadas = async () => {
    if (selected.size === 0) { toast.error('Selecciona al menos una herramienta'); return; }
    if (selected.size > 200) { toast.error('Máximo 200 etiquetas a la vez'); return; }
    try {
      const baseUrl = window.location.origin;
      const res = await api.get('/bodega/herramientas/qr-etiquetas', {
        params: { codigos: Array.from(selected).join(','), base_url: baseUrl },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `etiquetas_qr_${selected.size}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`PDF con ${selected.size} etiquetas descargado`);
    } catch {
      toast.error('Error al generar PDF');
    }
  };

  const descargarQRUna = async (codigo) => {
    try {
      const baseUrl = window.location.origin;
      const res = await api.get('/bodega/herramientas/qr-etiquetas', {
        params: { codigos: codigo, base_url: baseUrl }, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `etiqueta_${codigo}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Error al generar QR'); }
  };

  // Cmd+K
  useRegisterCmdKCommands(
    'maestro-herramientas',
    'Maestro Herramientas',
    [
      { id: 'mh-qr-sel', label: `Imprimir QR de ${selected.size} seleccionadas`, icon: 'Plus',
        run: descargarQRSeleccionadas, enabled: selected.size > 0 },
      { id: 'mh-filter-incompl', label: 'Filtrar: Descripciones incompletas (***)', icon: 'Filter',
        sublabel: `${stats.incompletas} herramientas`,
        run: () => setCalidadFilter('desc_incompleta'), enabled: calidadFilter !== 'desc_incompleta' },
      { id: 'mh-filter-sinfam', label: 'Filtrar: Sin familia asignada', icon: 'Filter',
        sublabel: `${stats.sin_familia} herramientas`,
        run: () => setCalidadFilter('sin_familia'), enabled: calidadFilter !== 'sin_familia' },
      { id: 'mh-filter-access', label: 'Filtrar: Solo importadas de Access', icon: 'Filter',
        sublabel: `${stats.access_import} herramientas`,
        run: () => setOrigenFilter('access_import'), enabled: origenFilter !== 'access_import' },
      { id: 'mh-clear', label: 'Limpiar filtros', icon: 'RefreshCw',
        run: () => { setFamiliaFilter(''); setOrigenFilter(''); setCalidadFilter(''); setQ(''); setQInput(''); },
        enabled: !!(familiaFilter || origenFilter || calidadFilter || q) },
      { id: 'mh-refresh', label: 'Refrescar', icon: 'RefreshCw', run: load },
    ],
    [selected.size, stats, calidadFilter, origenFilter, familiaFilter, q, load],
  );

  return (
    <div className="container mx-auto p-3 md:p-6 space-y-4" data-testid="page-maestro-herramientas">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Wrench className="w-6 h-6 text-amber-600" /> Maestro de Herramientas
          </h1>
          <p className="text-sm text-muted-foreground">
            {stats.total_htas || total} herramientas en total · importadas del Access + las nativas. Filtrá por familia, origen o calidad de datos.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {selected.size > 0 && (
            <Button
              size="sm"
              onClick={descargarQRSeleccionadas}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="btn-qr-seleccionadas"
            >
              <Printer className="w-4 h-4 mr-1" /> Imprimir QR ({selected.size})
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="btn-refresh-htas">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
          <Button size="sm" variant="outline" onClick={imprimirHtas} title="Imprimir listado" data-testid="btn-print-htas">
            <Printer className="w-4 h-4 mr-1" /> Imprimir
          </Button>
          <Button size="sm" variant="outline" onClick={exportarHtasExcel} className="text-emerald-700 border-emerald-300 hover:bg-emerald-50" title="Exportar a Excel" data-testid="btn-excel-htas">
            <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
          </Button>
        </div>
      </div>

      {/* Stats de calidad de datos */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Card className="border-amber-200">
          <CardContent className="p-3">
            <div className="text-[10px] uppercase text-muted-foreground flex items-center gap-1">
              <AlertCircle className="w-3 h-3" /> Descripción incompleta
            </div>
            <div className="text-2xl font-bold text-amber-700">{stats.incompletas}</div>
            <div className="text-[10px] text-muted-foreground">con *** o 3*</div>
          </CardContent>
        </Card>
        <Card className="border-orange-200">
          <CardContent className="p-3">
            <div className="text-[10px] uppercase text-muted-foreground">Sin familia</div>
            <div className="text-2xl font-bold text-orange-700">{stats.sin_familia}</div>
          </CardContent>
        </Card>
        <Card className="border-red-200">
          <CardContent className="p-3">
            <div className="text-[10px] uppercase text-muted-foreground flex items-center gap-1">
              <DollarSign className="w-3 h-3" /> Sin precio
            </div>
            <div className="text-2xl font-bold text-red-700">{stats.sin_precio}</div>
          </CardContent>
        </Card>
        <Card className="border-indigo-200">
          <CardContent className="p-3">
            <div className="text-[10px] uppercase text-muted-foreground">Importadas de Access</div>
            <div className="text-2xl font-bold text-indigo-700">{stats.access_import}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="p-3 space-y-2">
          <form onSubmit={(e) => { e.preventDefault(); setQ(qInput); }} className="flex gap-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-muted-foreground" />
              <Input
                className="pl-8"
                placeholder="Código o descripción..."
                value={qInput}
                onChange={e => setQInput(e.target.value)}
                data-testid="input-search-htas"
              />
            </div>
            <Button type="submit" size="sm" data-testid="btn-search-htas">Buscar</Button>
          </form>
          <div className="flex gap-2 flex-wrap items-center text-xs">
            <span className="text-muted-foreground flex items-center gap-1"><Filter className="w-3 h-3" /></span>
            <select className="h-7 rounded border bg-background px-2 text-xs" value={familiaFilter} onChange={e => setFamiliaFilter(e.target.value)} data-testid="select-familia">
              <option value="">Todas las familias</option>
              {familias.map(f => <option key={f.codigo} value={f.nombre}>{f.nombre}</option>)}
            </select>
            <select className="h-7 rounded border bg-background px-2 text-xs" value={origenFilter} onChange={e => setOrigenFilter(e.target.value)} data-testid="select-origen">
              <option value="">Todo origen</option>
              <option value="access_import">Solo Access</option>
              <option value="nativa">Solo nativas</option>
            </select>
            <select className="h-7 rounded border bg-background px-2 text-xs" value={calidadFilter} onChange={e => setCalidadFilter(e.target.value)} data-testid="select-calidad">
              <option value="">Toda calidad</option>
              <option value="desc_incompleta">Descripción incompleta (***)</option>
              <option value="sin_familia">Sin familia</option>
              <option value="sin_precio">Sin precio</option>
            </select>
            {(familiaFilter || origenFilter || calidadFilter || q) && (
              <button
                type="button"
                onClick={() => { setFamiliaFilter(''); setOrigenFilter(''); setCalidadFilter(''); setQ(''); setQInput(''); }}
                className="text-indigo-600 hover:underline"
                data-testid="btn-clear-filtros"
              >
                Limpiar
              </button>
            )}
            <span className="ml-auto text-muted-foreground">{items.length} resultados</span>
          </div>
        </CardContent>
      </Card>

      {/* Tabla */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
          ) : items.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground">Sin resultados</div>
          ) : (
            <div className="overflow-x-auto max-h-[70vh]">
              <table className="w-full text-xs" data-testid="table-htas">
                <thead className="sticky top-0 bg-slate-800 text-white text-[10px] uppercase">
                  <tr>
                    <th className="px-2 py-2 w-8">
                      <input
                        type="checkbox"
                        checked={items.length > 0 && items.every(m => selected.has(m.codigo))}
                        onChange={(e) => {
                          if (e.target.checked) setSelected(new Set(items.map(m => m.codigo)));
                          else setSelected(new Set());
                        }}
                        data-testid="cb-select-all"
                      />
                    </th>
                    <th className="text-left px-2 py-2">Código</th>
                    <th className="text-left px-2 py-2">Descripción</th>
                    <th className="text-left px-2 py-2">Familia</th>
                    <th className="text-right px-2 py-2">Stock</th>
                    <th className="text-right px-2 py-2">Precio</th>
                    <th className="text-left px-2 py-2">Últ. Ubic.</th>
                    <th className="text-left px-2 py-2">Últ. RUT</th>
                    <th className="text-center px-2 py-2">Origen</th>
                    <th className="text-center px-2 py-2 w-16">QR</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((m, idx) => {
                    const desc = m.descripcion2 || m.descripcion || '';
                    const incompleta = /\*{2,}|^3\*/.test(desc);
                    const isAccess = m.origen === 'access_import';
                    const ultubica = m.access_meta?.ULTUBICA || '';
                    const ultrut = m.access_meta?.ULTRUT || '';
                    return (
                      <tr
                        key={`${m.codigo}-${idx}`}
                        className={`border-b hover:bg-slate-50 dark:hover:bg-slate-900/50 ${selected.has(m.codigo) ? 'bg-indigo-50 dark:bg-indigo-950/30' : ''}`}
                        data-testid={`row-hta-${m.codigo}`}
                      >
                        <td className="px-2 py-1.5">
                          <input type="checkbox" checked={selected.has(m.codigo)} onChange={() => toggleSelect(m.codigo)} />
                        </td>
                        <td className="px-2 py-1.5 font-mono">{m.codigo}</td>
                        <td className="px-2 py-1.5">
                          <span className={incompleta ? 'text-amber-700' : ''}>{desc || '—'}</span>
                          {incompleta && <AlertCircle className="inline w-3 h-3 ml-1 text-amber-600" />}
                        </td>
                        <td className="px-2 py-1.5">
                          {m.familia_nombre || <span className="text-red-600 italic">sin familia</span>}
                        </td>
                        <td className="px-2 py-1.5 text-right tabular-nums">
                          <span className={m.stock_actual > 0 ? 'text-emerald-700 font-medium' : 'text-muted-foreground'}>
                            {fmtCLP(m.stock_actual)}
                          </span>
                        </td>
                        <td className="px-2 py-1.5 text-right tabular-nums">
                          {m.ultimo_precio > 0 ? `$${fmtCLP(m.ultimo_precio)}` : <span className="text-red-600 italic">—</span>}
                        </td>
                        <td className="px-2 py-1.5 text-muted-foreground truncate max-w-[130px]" title={ultubica}>{ultubica}</td>
                        <td className="px-2 py-1.5 font-mono text-muted-foreground text-[10px]">{ultrut}</td>
                        <td className="px-2 py-1.5 text-center">
                          {isAccess ? (
                            <Badge variant="outline" className="text-[9px] h-4 border-indigo-400 text-indigo-700">Access</Badge>
                          ) : (
                            <Badge variant="outline" className="text-[9px] h-4 border-emerald-400 text-emerald-700">Nativa</Badge>
                          )}
                        </td>
                        <td className="px-2 py-1.5 text-center">
                          <div className="flex gap-1 justify-center">
                            <button
                              onClick={() => descargarQRUna(m.codigo)}
                              className="text-indigo-600 hover:bg-indigo-50 p-1 rounded"
                              title="Descargar QR"
                              data-testid={`btn-qr-single-${m.codigo}`}
                            >
                              <QrCode className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => navigate(`/bodega/herramientas?q=${encodeURIComponent(m.codigo)}&tab=ubicacion`)}
                              className="text-slate-600 hover:bg-slate-50 p-1 rounded"
                              title="Ver ubicación"
                              data-testid={`btn-ubic-${m.codigo}`}
                            >
                              <MapPin className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => navigate(`/bodega/herramientas/${encodeURIComponent(m.codigo)}/timeline`)}
                              className="text-indigo-700 hover:bg-indigo-50 p-1 rounded"
                              title="Ver timeline / trazabilidad"
                              data-testid={`btn-timeline-${m.codigo}`}
                            >
                              <History className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
